/**
 * CC-024 (2026-09-25) — Stale draft payroll runs admin page.
 *
 * Platform-admin only. Two-step flow: LOAD PREVIEW → REVIEW LIST →
 * CONFIRM + REASON → DELETE. Preview + delete both audit-log through
 * the backend; the delete endpoint re-verifies each run's stale-eligibility
 * at request time so anything that changed state between preview and
 * confirm is skipped and reported back rather than mistakenly deleted.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, Trash2, RefreshCw, ShieldCheck } from "lucide-react";
import api from "../../lib/api";

export default function AdminPayrollStaleDrafts() {
  const { slug } = useParams();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    setBusy("preview"); setErr(null); setResult(null);
    try {
      const { data } = await api.get(
        `/payroll/admin/stale-drafts?tenant_slug=${slug}`,
      );
      setPreview(data);
      // Pre-select everything eligible so the default action is
      // "delete all"; Brett can uncheck individual rows if desired.
      setSelected(new Set((data.eligible_for_deletion || []).map((r) => r.id)));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally { setBusy(null); }
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const doDelete = async () => {
    if (reason.trim().length < 20) return;
    if (!selected.size) return;
    setBusy("delete"); setErr(null);
    try {
      const { data } = await api.post(
        `/payroll/admin/stale-drafts/delete?tenant_slug=${slug}`,
        {
          run_ids: [...selected],
          confirm: true,
          reason: reason.trim(),
        },
      );
      setResult(data);
      setConfirmOpen(false);
      setReason("");
      setSelected(new Set());
      // Refresh preview so the just-deleted rows disappear.
      await loadPreview();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d?.message || d || e.message);
    } finally { setBusy(null); }
  };

  return (
    <div className="p-6 space-y-5 max-w-[1100px]" data-testid="admin-payroll-stale-drafts">
      <div>
        <h1 className="font-display font-bold text-[22px] text-[#0e1420]">
          Payroll · Stale Draft Cleanup <span className="text-[13px] font-normal text-[color:var(--cc-ink-muted)] italic">CC-024</span>
        </h1>
        <p className="text-[13px] text-[color:var(--cc-ink-muted)] mt-1 max-w-[720px]">
          Delete draft runs older than the current pay week that were <b>never</b> approved,
          approval-overridden, sent-for-approval, sent-to-provider, or tech-reviewed. Preview
          the list first; nothing deletes until you type a written reason and confirm.
        </p>
      </div>

      <div className="flex gap-2 items-center">
        <button
          type="button"
          onClick={loadPreview}
          disabled={busy === "preview"}
          data-testid="admin-payroll-stale-drafts-load-btn"
          className="bg-[#0072bc] text-white text-[13px] font-semibold px-3 py-2 rounded-lg hover:bg-[#005f9c] disabled:opacity-50 flex items-center gap-2"
        >
          {busy === "preview" ? <><RefreshCw size={14} className="animate-spin" /> Loading…</> : "Load preview"}
        </button>
        {preview && (
          <span className="text-[12.5px] text-[color:var(--cc-ink-muted)]" data-testid="admin-payroll-stale-drafts-current-pw">
            Current pay week Monday: <span className="font-mono-data font-semibold">{preview.current_pay_week_monday}</span>
          </span>
        )}
      </div>

      {err && (
        <div className="bg-[#fdecec] border border-[#f7c1c1] text-[#7a1c1c] rounded-lg px-3 py-2 text-[12.5px] flex items-start gap-2" data-testid="admin-payroll-stale-drafts-err">
          <AlertCircle size={14} className="mt-0.5" /> <span>{typeof err === "string" ? err : JSON.stringify(err)}</span>
        </div>
      )}

      {result && (
        <div className="bg-[#e6f9f2] border border-[#8fd4b7] text-[#0e6b52] rounded-lg px-3 py-2 text-[12.5px] flex items-start gap-2" data-testid="admin-payroll-stale-drafts-result">
          <ShieldCheck size={14} className="mt-0.5" />
          <span>
            Deleted {result.deleted_count} run(s), skipped {result.skipped_count}. Audit log recorded.
          </span>
        </div>
      )}

      {preview && preview.eligible_for_deletion?.length > 0 && (
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#eef1f6] flex items-center justify-between">
            <span className="font-display font-semibold text-[14px]">
              Eligible for deletion · {preview.eligible_count}
            </span>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!selected.size}
              data-testid="admin-payroll-stale-drafts-delete-btn"
              className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-[#a01d1d] text-white hover:bg-[#7a1c1c] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <Trash2 size={13} /> Delete {selected.size} selected
            </button>
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left py-2 px-4 w-[40px]"></th>
                <th className="text-left py-2 px-4">Run ID</th>
                <th className="text-left py-2 px-4">Pay week</th>
                <th className="text-left py-2 px-4">Status</th>
                <th className="text-left py-2 px-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {preview.eligible_for_deletion.map((r) => (
                <tr key={r.id} className="border-t border-[#eef1f6]" data-testid={`admin-payroll-stale-drafts-row-${r.id}`}>
                  <td className="py-2 px-4">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      data-testid={`admin-payroll-stale-drafts-check-${r.id}`}
                    />
                  </td>
                  <td className="py-2 px-4 font-mono-data font-bold">{r.id}</td>
                  <td className="py-2 px-4 font-mono-data">{r.pay_week_monday} → {r.pay_week_sunday}</td>
                  <td className="py-2 px-4">
                    <span className="text-[10.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#f5f6f8] text-[color:var(--cc-ink-muted)]">
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-[color:var(--cc-ink-muted)]">
                    {r.created_at?.slice(0,16).replace("T"," ")} by {r.created_by}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && preview.eligible_for_deletion?.length === 0 && (
        <div className="text-[13px] text-[color:var(--cc-ink-muted)] italic" data-testid="admin-payroll-stale-drafts-empty">
          Nothing to delete — no stale drafts older than the current pay week.
        </div>
      )}

      {preview && preview.ineligible_but_older_than_current?.length > 0 && (
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#eef1f6] font-display font-semibold text-[14px] text-[color:var(--cc-ink-muted)]">
            Older than current but PROTECTED · {preview.ineligible_count}
          </div>
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left py-2 px-4">Run ID</th>
                <th className="text-left py-2 px-4">Pay week</th>
                <th className="text-left py-2 px-4">Why protected</th>
              </tr>
            </thead>
            <tbody>
              {preview.ineligible_but_older_than_current.map((r) => (
                <tr key={r.id} className="border-t border-[#eef1f6]">
                  <td className="py-2 px-4 font-mono-data">{r.id}</td>
                  <td className="py-2 px-4 font-mono-data">{r.pay_week_monday}</td>
                  <td className="py-2 px-4">
                    <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-[#e0f4ec] text-[#0e6b52]">
                      {r.ineligible_reason}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          data-testid="admin-payroll-stale-drafts-confirm-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-[560px] w-[92vw] p-6 border-2 border-[#a01d1d]">
            <div className="font-display font-bold text-[15px] text-[#a01d1d] mb-2">
              Confirm delete · {selected.size} run(s)
            </div>
            <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mb-3">
              This deletes the runs and their associated source files, payouts, and exceptions.
              The server re-verifies each id at request time; anything no longer stale will be skipped.
            </div>
            <label className="block text-[12px] font-semibold text-[#0e1420] mb-1">
              Written justification (audit-logged, ≥ 20 characters)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="admin-payroll-stale-drafts-reason-textarea"
              className="w-full border border-[#cfd4dc] rounded px-3 py-2 text-[13px] font-mono-data resize-none focus:outline-none focus:border-[#a01d1d]"
              placeholder="e.g. Friday cleanup of abandoned Q3 drafts per Brett — nothing was ever approved or sent."
            />
            <div className="flex items-center justify-between mt-1 text-[11px]">
              <span className={reason.trim().length < 20 ? "text-[#a01d1d]" : "text-[color:var(--cc-ink-muted)]"}>
                {reason.trim().length}/20 min
              </span>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setConfirmOpen(false); setReason(""); }}
                disabled={busy === "delete"}
                data-testid="admin-payroll-stale-drafts-cancel-btn"
                className="text-[12.5px] px-3 py-1.5 rounded border border-[#cfd4dc] font-semibold text-[#0e1420] hover:bg-[#f4f6fa] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={reason.trim().length < 20 || busy === "delete"}
                data-testid="admin-payroll-stale-drafts-confirm-btn"
                className="text-[12.5px] px-3 py-1.5 rounded font-semibold text-white bg-[#a01d1d] hover:bg-[#7a1c1c] disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Trash2 size={13} />
                {busy === "delete" ? "Deleting…" : `Delete ${selected.size} run(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
